import React from 'react';
import PropTypes from 'prop-types';

import {Grid, Popper, IconButton, Tooltip, CircularProgress, Popover, Zoom} from 'views/components';
import {ContentCopy as ContentCopyIcon, Edit as EditIcon, Delete as DeleteIcon, InsertEmoticon, Reply as ReplyIcon} from 'views/icons';
import {translate} from 'views/module';

import EmojiPicker from './emoji/EmojiPicker';
import DeleteConfirmDialog from './DeleteConfirmDialog';

const HoverToolBox = (props) => {
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [openEmojiPicker, setOpenEmojiPicker] = React.useState(false);

  const {
    toolBar, // Toolbar visible or not visible
    anchorEl, // Element that toolbar hovers over
    copiedText, // Text of ticket comment that toolBox is currently hovering over
    onClickEdit, // function to handle edit button clicked
    onClickDelete, // function to handle delete button clicked
    onClickReply,
    onEmojiSelected,
    onMouseEnter,
    onMouseLeave,
    marginTop=8,
    hideTool={},
  } = props;

  // Change color of copy button when clicked
  const [copyButton, setCopyButton] = React.useState(false);

  const emojiPickerAnchorRef = React.useRef();
  const iconSx = {color: '#A8A49E'};

  // Copy ticket comment to clipboard
  const copyComment = () => {
    setCopyButton(true);
    return window.navigator.clipboard.writeText(copiedText);
  };

  const onClickDeleteWrapper = () => {
    setDialogOpen(true);
  };

  const onClickConfirmDelete = () => {
    onClickDelete(setIsDeleting);
    setDialogOpen(false);
  };

  const onEmojiSelect = (selectedEmoji) => {
    if (onEmojiSelected) {
      onEmojiSelected(selectedEmoji.native);
    }
    setOpenEmojiPicker(false);
  };

  const toolElements = {
    emoji: <Grid key='emoji' item>
      <Tooltip title={translate('correspondances.ToolBar.Emoji')} placement="top"
        TransitionComponent={Zoom}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -10],
              },
            },
          ],
        }}>
        <IconButton ref={emojiPickerAnchorRef} onClick={() => setOpenEmojiPicker((prev) => !prev)} data-testid='emojiButton'>
          <InsertEmoticon sx={iconSx}/>
        </IconButton>
      </Tooltip>
    </Grid>,
    reply: <Grid key='reply' item>
      <Tooltip title={translate('correspondances.ToolBar.Reply')} placement="top"
        TransitionComponent={Zoom}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -10],
              },
            },
          ],
        }}>
        <IconButton onClick={onClickReply} data-testid='replyButton'>
          <ReplyIcon sx={iconSx}/>
        </IconButton>
      </Tooltip>
    </Grid>,
    copy: <Grid key='copy' item>
      <Tooltip title={translate('correspondances.ToolBar.Copy')} placement="top"
        TransitionComponent={Zoom}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -10],
              },
            },
          ],
        }}>
        <IconButton onClick={copyComment} data-id='copyButton'>
          <ContentCopyIcon sx={{color: copyButton ? 'secondary.main' : '#A8A49E'}}/>
        </IconButton>
      </Tooltip>
    </Grid>,
    edit: <Grid key='edit' item>
      <Tooltip title={translate('correspondances.ToolBar.Edit')} placement="top"
        TransitionComponent={Zoom}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -10],
              },
            },
          ],
        }}
      >
        <IconButton data-testid='editButton' onClick={onClickEdit}>
          <EditIcon sx={iconSx}/>
        </IconButton>
      </Tooltip>
    </Grid>,
    delete: <Grid key='delete' item>
      <Tooltip title={translate('correspondances.ToolBar.Delete')} placement="top"
        TransitionComponent={Zoom}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -10],
              },
            },
          ],
        }}
      >
        <span>
          <IconButton data-testid='deleteButton' disabled={isDeleting} onClick={onClickDeleteWrapper}>
            {
          isDeleting ?
            <CircularProgress size='1rem'/> :
            <DeleteIcon sx={iconSx}/>
            }
          </IconButton>
        </span>
      </Tooltip>
    </Grid>,
  };

  // Reset color of tool bar buttons when mouse leaves comment/announcement
  React.useEffect(() => {
    setCopyButton(false);
  }, [anchorEl, toolBar]);

  const calculateToolbarOffset = React.useCallback(() => {
    const totalNumTools = Object.values(toolElements).length;
    const sizeOfButton = 40;
    const marginRight = 8;
    if (!hideTool) {
      return (totalNumTools * -sizeOfButton) - marginRight;
    }
    const numhiddenTools = Object.values(hideTool).filter(Boolean).length;
    return (totalNumTools * -sizeOfButton) + (numhiddenTools * sizeOfButton) - marginRight;
  }, [hideTool]);

  return (
    <>
      <Popper
        open={toolBar}
        onClickAway={() => {}} // Custom popper requires clickAway function but not applicable here
        anchorEl={anchorEl}
        placement='right-start'
        popperModifiers={[ // Position popper to appear in top right corner of ticket comment
          {
            name: 'offset',
            options: {
              offset: [marginTop, calculateToolbarOffset()],
            },
          },
        ]}
      >
        <Grid container onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
          {
            Object.keys(toolElements).map((tool) => {
              if (hideTool[tool]) {
                return;
              }
              return toolElements[tool];
            })
          }
        </Grid>
      </Popper>
      <Popover
        open={openEmojiPicker}
        anchorEl={emojiPickerAnchorRef.current}
        onClose={() => setOpenEmojiPicker(false)}
        disableRestoreFocus // helpful to prevent scroll jumping to top when closing popover
      >
        <EmojiPicker
          onEmojiSelect={onEmojiSelect}
        />
      </Popover>
      {
        dialogOpen &&
        <DeleteConfirmDialog setDialogOpen={setDialogOpen} onClickConfirmDelete={onClickConfirmDelete}/>
      }
    </>
  );
};

HoverToolBox.propTypes = {
  toolBar: PropTypes.bool,
  anchorEl: PropTypes.object,
  copiedText: PropTypes.string,
  onClickEdit: PropTypes.func,
  onClickDelete: PropTypes.func,
  onClickReply: PropTypes.func,
  onEmojiSelected: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  marginTop: PropTypes.number,
  hideTool: PropTypes.shape({
    emoji: PropTypes.bool,
    reply: PropTypes.bool,
    copy: PropTypes.bool,
    edit: PropTypes.bool,
    delete: PropTypes.bool,
  }),
};

export default React.memo(HoverToolBox);
